import { Button, Menu, MenuItem, Stack, Typography } from '@mui/material'
import debounce from 'lodash/debounce'
import {
  ChangeEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Icons, ScrollTag, Search } from 'utoniq-ui'

import {
  EventPickupUsersOptions,
  OrderBy,
  PickupTicketStatus,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { Organism, OrganismFrame } from '@/core/architecture'
import { useAnalytics } from '@/lib/analytics'

type SearchFormProps = {
  artistId: string
  onSubmit: (values: EventPickupUsersOptions) => void
}

export const SearchForm: Organism<SearchFormProps> = ({
  onSubmit,
  artistId,
}) => {
  const { t } = useTranslation('consoleTicketReceives')
  const { router } =
    useRouter<{ type: string; eventId: string; collectionId: string }>()
  const { analytics } = useAnalytics()
  const [options, setOptions] = useState<EventPickupUsersOptions>({
    type: PickupTicketStatus.All,
  })
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedIndex, setSelectedIndex] = useState<number>(0)
  const debounceChangeKeyword = useRef(
    debounce((opts: EventPickupUsersOptions) => {
      onSubmit(opts)
    }, 300)
  )
  const filterOptions = [
    { label: t('pickupTimeOrderAsc'), value: OrderBy.Asc },
    { label: t('pickupTimeOrderDesc'), value: OrderBy.Desc },
  ]

  useEffect(() => {
    setOptions({
      type: PickupTicketStatus.All,
      isHistory: !!router.query.type,
      searchTerm: '',
      orderBy: undefined,
    })
  }, [router.pathname])

  const handleClickListItem = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleMenuItemClick = useCallback(
    (event: MouseEvent<HTMLElement>, index: number) => {
      setSelectedIndex(index)
      setAnchorEl(null)
    },
    []
  )

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const handleChangeKeyword = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setOptions(prevState => ({
        ...prevState,
        searchTerm: event.target.value,
      }))
      debounceChangeKeyword.current({
        ...options,
        searchTerm: event.target.value,
      })
    },
    [debounceChangeKeyword, options]
  )

  return (
    <OrganismFrame>
      <Search
        value={options.searchTerm}
        fullWidth
        placeholder={t('searchPlaceholder')}
        onChange={handleChangeKeyword}
        size="small"
        sx={{
          minHeight: 40,
          mb: 2,
          bgcolor: ({ palette }) => `${palette.grey[50]}!important`,
          '.MuiInputBase-root, input': {
            bgcolor: 'unset',
          },
        }}
        onFocus={() => {
          analytics?.track('click_pickupSearch', {
            applicationKey: config.applicationKey,
            artistId,
            eventId: router.query.eventId,
            collectionId: router.query.collectionId,
          })
        }}
      />
      <ScrollTag
        value={options.type ?? undefined}
        tags={[
          { label: t('all', 'すべて'), value: PickupTicketStatus.All },
          { label: t('unused', '未使用'), value: PickupTicketStatus.Unused },
          {
            label: t('alreadyUsed', '使用済'),
            value: PickupTicketStatus.Used,
          },
        ]}
        onClick={val => {
          setOptions(prevState => ({ ...prevState, type: val }))
          onSubmit({ ...options, type: val })
        }}
      />
      <Stack direction="row-reverse">
        <Button
          variant="text"
          onClick={handleClickListItem}
          sx={{ mr: -1 }}
          endIcon={<Icons.DownArrow fontSize="xSmall" />}
        >
          <Typography variant="caption">
            {filterOptions[selectedIndex]?.label}
          </Typography>
        </Button>
      </Stack>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        {filterOptions.map((option, index) => (
          <MenuItem
            key={index}
            selected={index === selectedIndex}
            sx={{ minWidth: 200 }}
            onClick={event => {
              handleMenuItemClick(event, index)
              setOptions(prevState => ({ ...prevState, orderBy: option.value }))
              onSubmit({ ...options, orderBy: option.value })
            }}
          >
            <Typography variant="body2">{option.label}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </OrganismFrame>
  )
}
